import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Device } from '@ionic-native/device/ngx';
import { domToCanvas } from 'modern-screenshot';
import { SettingsService } from '../services/settings.service';

export interface ScreenshotCommandPayload {
  // Scaled down to this width, default 1280.
  maxWidth?: number;
  // JPEG quality 0.1..1, default 0.7.
  quality?: number;
}

export interface ScreenshotResult {
  container: string;
  name: string;
  size: number;
  width: number;
  height: number;
  url: string;
  captureMs: number;
  uploadMs: number;
}

// The nginx in front of Nt.Pos.Api takes 1 MB per request: keep the base64 JSON well under it.
const MAX_BASE64_LENGTH = 650 * 1024;
const QUALITY_STEPS = [1, 0.75, 0.55, 0.4];
const SCALE_STEPS = [1, 0.75, 0.5];
// Drawn instead of images the browser does not let us read (cross-origin), instead of failing.
const IMAGE_PLACEHOLDER =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

// Takes a screenshot of the app (the DOM, with open modals) and uploads it to Nt.Pos.Api,
// which stores it in the device's blob container for the admin tool.
//
// modern-screenshot, not dom-to-image: Ionic draws its components in shadow DOM, which dom-to-image
// does not copy (toolbars, items and badges came out empty), and dom-to-image writes every computed
// style inline, so a screen grows past the ~2 MB data: URL WebViews load and fails with a bare "error".
@Injectable({ providedIn: 'root' })
export class PosTerminalScreenshotService {
  constructor(
    private _http: HttpClient,
    private _settings: SettingsService,
    private _device: Device,
    private _router: Router,
  ) {}

  async capture(payload: ScreenshotCommandPayload, commandId?: string): Promise<ScreenshotResult> {
    const maxWidth = this._clamp(payload?.maxWidth, 320, 2560, 1280);
    const quality = this._clamp(payload?.quality, 0.1, 1, 0.7);
    const url = this._router.url;

    const started = performance.now();
    const screen = await this._render(Math.min(1, maxWidth / window.innerWidth));
    const { dataUrl, width, height } = this._encode(screen, quality);
    const captureMs = Math.round(performance.now() - started);

    const uploadStarted = performance.now();
    const saved = await this._http
      .post<{ container: string; name: string; size: number }>(
        `${this._settings.pos_api_endpoint}/terminal/${encodeURIComponent(this._device.uuid)}/screenshots`,
        {
          contentType: 'image/jpeg',
          imageBase64: dataUrl.substring(dataUrl.indexOf(',') + 1),
          commandId,
          url,
          width,
          height,
        },
        { params: new HttpParams().set('responseFormater', 'none') },
      )
      .toPromise();

    return {
      container: saved.container,
      name: saved.name,
      size: saved.size,
      width,
      height,
      url,
      captureMs,
      uploadMs: Math.round(performance.now() - uploadStarted),
    };
  }

  // What is on screen (not the whole scrollable document), already at the target scale.
  private async _render(scale: number): Promise<HTMLCanvasElement> {
    try {
      return await domToCanvas(document.body, {
        width: window.innerWidth,
        height: window.innerHeight,
        scale,
        backgroundColor: 'white',
        filter: (node: Node) => this._isCapturable(node),
        fetch: { placeholderImage: IMAGE_PLACEHOLDER },
      });
    } catch (err) {
      // An image that fails inside the library rejects with a bare DOM Event.
      throw new Error(`Screenshot capture failed: ${this._describe(err)}`);
    }
  }

  // JPEG at the requested quality, then lower quality and size until it fits the upload limit.
  private _encode(screen: HTMLCanvasElement, quality: number) {
    let last: { dataUrl: string; width: number; height: number };

    for (const scaleStep of SCALE_STEPS) {
      const canvas = scaleStep === 1 ? screen : this._scaled(screen, scaleStep);

      for (const qualityStep of QUALITY_STEPS) {
        let dataUrl: string;
        try {
          dataUrl = canvas.toDataURL('image/jpeg', Math.max(0.1, quality * qualityStep));
        } catch (err) {
          // WebKit (iOS) taints a canvas that drew an SVG with HTML inside.
          throw new Error(`Screenshot can not be read back on this device: ${this._describe(err)}`);
        }

        last = { dataUrl, width: canvas.width, height: canvas.height };
        if (dataUrl.length - dataUrl.indexOf(',') - 1 <= MAX_BASE64_LENGTH) {
          return last;
        }
      }
    }

    throw new Error(
      `Screenshot is too large to upload even at ${last.width}x${last.height} (${Math.round(last.dataUrl.length / 1024)} KB)`,
    );
  }

  private _scaled(source: HTMLCanvasElement, factor: number): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(source.width * factor));
    canvas.height = Math.max(1, Math.round(source.height * factor));
    const context = canvas.getContext('2d');
    context.fillStyle = 'white';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(source, 0, 0, canvas.width, canvas.height);
    return canvas;
  }

  // An <img> without src (the library would fetch the page itself) or one that failed to load
  // breaks the whole capture: leave those out.
  private _isCapturable(node: Node): boolean {
    if (!(node instanceof HTMLImageElement)) {
      return true;
    }

    return !!node.getAttribute('src') && (!node.complete || node.naturalWidth > 0);
  }

  private _describe(err: unknown): string {
    if (err instanceof Error) {
      return err.message;
    }

    const event = err as Event;
    const target = event?.target as HTMLImageElement | undefined;
    if (event?.type) {
      const src = target?.src ? ` ${String(target.src).slice(0, 80)}` : '';
      return `${event.type} on ${target?.tagName || 'element'}${src}`;
    }

    return String(err);
  }

  private _clamp(value: unknown, min: number, max: number, fallback: number): number {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? Math.min(max, Math.max(min, number)) : fallback;
  }
}
